import { createReducer, on, Action } from '@ngrx/store';
import { TicketsScope, TicketsState } from './tickets.model';
import {
  loadListDispatcher,
  loadTicketList,
  loadSyndicateTicketList,
  loadGTTicketList,
  callTicketList,
  callSyndicateTicketList,
  callGTList,
  successList,
  failTicketList,
  listLoadMore,
  syndicateListLoadMore,
  gtTicketListLoadMore,
  successTicket,
  successGTTicket,
  failTicket,
  successCreateTicket,
  successShareTicket,
  callSoloTicket,
  callSyndicateTicket,
  callGTTicket,
  clearCurrentTicket,
  clearGroupTicket
} from './tickets.actions';
import { isEmpty } from 'lodash-es';
import { callSyndicate } from '../syndicates/syndicates.actions';
import {
  singleTicketAdapter,
  syndicateTicketAdapter,
  groupTicketAdapter,
  initialSingleTicketState,
  initialSyndicateTicketState,
  initialGroupTicketState
} from './tickets.adapter';

const createEmptyListState = (): TicketsScope.ListState => ({
  items: [],
  totalItems: 0,
  isLoading: false,
  pagination: {
    type: 'all',
    page: 1,
    per: 20,
    filters: {
      is_solo_bet: true,
      with_offer: false,
      status: 'live'
    }
  }
});

export const initialState: TicketsState = {
  // Legacy list
  list: undefined,

  // Entity-managed lists
  singleTickets: initialSingleTicketState,
  syndicateTickets: initialSyndicateTicketState,
  groupTickets: initialGroupTicketState,

  // Legacy lists field
  lists: {
    single: createEmptyListState(),
    syndicate: createEmptyListState(),
    group: createEmptyListState()
  },

  current_ticket: undefined,
  group_ticket: undefined,
  pagination: {
    type: 'all',
    page: 1,
    per: 20,
    filters: {
      is_solo_bet: true,
      with_offer: false,
      status: 'live'
    }
  },
  isLoading: false,
  isSyndicate: false,
  unauthSecurityCode: undefined,
  ticketLoadError: false
};

const reducer = createReducer(
  initialState,
  // Legacy action handlers
  on(loadListDispatcher, (state) => ({
    ...state,
    list: {
      items: [],
      total_items: 0
    },
    isLoading: true
  })),

  on(loadTicketList, loadSyndicateTicketList, (state, action) => {
    // Use action.type string comparison — comparing the action object to the creator
    // function with === would always be false (pre-existing bug, now surfaced by entity selectors).
    const isSyndicate = action.type === loadSyndicateTicketList.type;

    // Clear the active entity's items and mark isLoading=true.
    // This prevents stale items (including GroupTicketListItem objects that may have
    // landed in singleTickets from a group load) from rendering while the request
    // is in flight. The @if(isLoading()) guard in the template relies on this.
    const updatedSingle = isSyndicate
      ? state.singleTickets
      : singleTicketAdapter.removeAll({
          ...state.singleTickets,
          isLoading: true,
          totalItems: 0,
        });
    const updatedSyndicate = isSyndicate
      ? syndicateTicketAdapter.removeAll({
          ...state.syndicateTickets,
          isLoading: true,
          totalItems: 0,
        })
      : state.syndicateTickets;

    return {
      ...state,
      isLoading: true,
      isSyndicate,
      singleTickets: updatedSingle,
      syndicateTickets: updatedSyndicate,
      pagination: {
        ...initialState.pagination,
        filters: Object.assign({}, initialState.pagination.filters, action.model?.filters),
      },
    };
  }),

  on(loadGTTicketList, (state, action) => ({
    ...state,
    isLoading: true,
    isSyndicate: false,
    pagination: {
      ...state.pagination,
      type: action.model?.type || 'all'
    }
  })),

  on(callTicketList, callSyndicateTicketList, callGTList, (state, action) => {
    let filters = null;
    if (action.model?.filters) {
      filters = Object.assign({}, state.pagination.filters, action.model.filters);
    }

    const pagination = isEmpty(action.model) ?
      Object.assign({}, state.pagination, { page: 1 }) :
      Object.assign({}, state.pagination, action.model, { filters: filters || state.pagination.filters });

    return {
      ...state,
      pagination
    };
  }),

  on(listLoadMore, syndicateListLoadMore, gtTicketListLoadMore, (state) => ({
    ...state,
    isLoading: true,
    pagination: Object.assign({}, state.pagination, { page: state.pagination.page + 1 })
  })),

  on(successList, (state, action) => {
    // Base list type from state flags
    const stateListType = state.isSyndicate
      ? 'syndicate'
      : state.pagination.type === 'invited'
        ? 'group'
        : 'single';

    // Shape-based guard: if the GT module dispatches successList with GroupTicketListItem
    // data but without setting pagination.type='invited' (e.g. loadGTTicketList({})),
    // the items would incorrectly land in singleTickets. Detect the actual item shape
    // and override listType to 'group' when needed.
    const firstItem = action.response.items?.[0] as any;
    const isGroupShape =
      firstItem != null &&
      'invited_count' in firstItem &&
      !('selections' in firstItem);
    const listType =
      isGroupShape && stateListType !== 'syndicate' ? 'group' : stateListType;

    const isLoadMore = state.pagination.page > 1;

    // Handle pagination for legacy list
    let legacyItems;
    if (isLoadMore && state.list?.items) {
      legacyItems = [...state.list.items, ...action.response.items];
    } else {
      legacyItems = action.response.items;
    }

    const legacyList: TicketsScope.TicketList = {
      items: legacyItems,
      total_items: action.response.total_items
    };

    // Handle entity-managed lists
    let updatedSingleTickets = state.singleTickets;
    let updatedSyndicateTickets = state.syndicateTickets;
    let updatedGroupTickets = state.groupTickets;

    if (listType === 'single') {
      const tickets = action.response.items as TicketsScope.TicketItem[];
      updatedSingleTickets = isLoadMore
        ? singleTicketAdapter.addMany(tickets, { ...state.singleTickets, isLoading: false, totalItems: action.response.total_items })
        : singleTicketAdapter.setAll(tickets, { ...state.singleTickets, isLoading: false, totalItems: action.response.total_items });
    } else if (listType === 'syndicate') {
      const tickets = action.response.items as TicketsScope.TicketItem[];
      updatedSyndicateTickets = isLoadMore
        ? syndicateTicketAdapter.addMany(tickets, { ...state.syndicateTickets, isLoading: false, totalItems: action.response.total_items })
        : syndicateTicketAdapter.setAll(tickets, { ...state.syndicateTickets, isLoading: false, totalItems: action.response.total_items });
    } else {
      const tickets = action.response.items as TicketsScope.GroupTicketListItem[];
      updatedGroupTickets = isLoadMore
        ? groupTicketAdapter.addMany(tickets, { ...state.groupTickets, isLoading: false, totalItems: action.response.total_items })
        : groupTicketAdapter.setAll(tickets, { ...state.groupTickets, isLoading: false, totalItems: action.response.total_items });
    }

    // Legacy lists for backward compatibility
    const typedItems = listType === 'group'
      ? legacyItems as TicketsScope.GroupTicketListItem[]
      : legacyItems as TicketsScope.TicketItem[];

    return {
      ...state,
      isLoading: false,
      singleTickets: updatedSingleTickets,
      syndicateTickets: updatedSyndicateTickets,
      groupTickets: updatedGroupTickets,
      list: legacyList,
      lists: {
        ...state.lists,
        [listType]: {
          items: typedItems,
          totalItems: action.response.total_items,
          isLoading: false,
          pagination: state.pagination
        }
      }
    };
  }),

  on(failTicketList, failTicket, (state) => ({
    ...state,
    isLoading: false,
    ticketLoadError: true
  })),

  on(successTicket, (state, action) => ({
    ...state,
    current_ticket: action.response
  })),

  on(successGTTicket, (state, action) => ({
    ...state,
    isLoading: false,
    group_ticket: action.response
  })),

  on(successCreateTicket, successShareTicket, (state, action) => ({
    ...state,
    response: action.model
  })),

  on(callSyndicate, (state) => ({
    ...state,
    current_ticket: undefined
  })),

  on(callSoloTicket, callSyndicateTicket, (state) => ({
    ...state,
    isLoading: true,
    current_ticket: undefined
  })),

  on(callGTTicket, (state) => ({
    ...state,
    isLoading: true,
    group_ticket: undefined
  })),

  on(clearCurrentTicket, (state) => ({
    ...state,
    current_ticket: undefined
  })),

  on(clearGroupTicket, (state) => ({
    ...state,
    group_ticket: undefined
  }))
);

export const ws = {
  RELOAD_EVENTS: [
    'POOL_STATUS_CHANGED',
    'UNIT_DISTRIBUTION_CHANGED',
    'POOL_UPDATED',
    'USER_NEW_OFFER',
    'USER_REMOVE_OFFER',
    'MICROSITE_AUTHENTICATION_REFRESH'
  ]
};

export function ticketReducer(
  state: TicketsState | undefined,
  action: Action
): TicketsState {
  return reducer(state, action);
}
